import React from "react";
import { Link } from "react-router-dom";
import ProgressiveImage from "../Ui/ProgressiveImage/ProgressiveImage";
import { useTranslation } from "../../hooks/useTranslation";
import { guideDetailPath, postTypeLabel } from "../../utils/posts";
import placeholderLogo from "../../images/mcdonalds-logo.png";

const formatPublishDate = (isoDate, language) => {
  if (!isoDate) return "";
  const parsed = new Date(isoDate);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleDateString(language === "zh" ? "zh-CN" : "en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

/**
 * Editorial card for a single published Guide (Post). Reusable across
 * /guides, /guides/:city and the homepage Latest Guides section — purpose
 * built for Guides, not a generic content card (mirrors the PicksSection
 * place-card layout so the two editorial surfaces feel like one system).
 *
 * `compact` trims the metadata line down to just the city (no publish date)
 * and drops tag chips — used on the homepage, where the spec calls for
 * more restrained metadata than the dedicated /guides listing. Default
 * (false) behavior is unchanged from Phase 2.
 *
 * @component
 */
const GuideCard = ({ post, compact = false }) => {
  const { t, language } = useTranslation();
  if (!post) return null;

  const { title, excerpt, city, postType, heroImageUrl, slug, tags, publishedAt } = post;
  const publishLabel = formatPublishDate(publishedAt, language);

  return (
    <Link
      to={guideDetailPath(city, slug)}
      className="card elev-sm sp-guide-card"
      style={{
        padding: 0,
        overflow: "hidden",
        textDecoration: "none",
        color: "inherit",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div className="sp-card-media" style={{ borderRadius: 0, height: 200, position: "relative" }}>
        <ProgressiveImage
          src={heroImageUrl || placeholderLogo}
          alt={title}
          fallbackSrc={placeholderLogo}
          loading="lazy"
          fill
          imgClassName="washed"
        />
        <span className="tag tag-accent" style={{ position: "absolute", top: 14, left: 14 }}>
          {postTypeLabel(t, postType)}
        </span>
      </div>
      <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 8, flex: 1 }}>
        <div style={{ fontSize: 12, opacity: 0.6, letterSpacing: "0.03em", display: "flex", gap: 6, alignItems: "center" }}>
          <span>{city}</span>
          {!compact && publishLabel && (
            <>
              <span aria-hidden="true">{t.guideMetaSeparator}</span>
              <span>{publishLabel}</span>
            </>
          )}
        </div>
        <div className="card-title" style={{ fontSize: 20 }}>{title}</div>
        {excerpt && (
          <p className="card-body" style={{ fontSize: 14, lineHeight: 1.55, flex: 1 }}>{excerpt}</p>
        )}
        {!compact && tags?.length > 0 && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 2 }}>
            {tags.slice(0, 3).map((tag) => (
              <span key={tag} className="tag tag-outline" style={{ fontSize: 11 }}>{tag}</span>
            ))}
          </div>
        )}
      </div>
    </Link>
  );
};

export default GuideCard;
